import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { KINETIC_WORD_SETTING } from './kineticWordSetting';
import { check } from '../skillUsage';

export const particleWordFormSkill: CodeoSkill = {
    name: SKILL_NAMES.particleWordForm,
    kind: 'codeo',
    description:
        'Form a word out of thousands of glowing particles that churn as a cloud and then stream ' +
        'into the letters, left to right — for a word about gathering, crowds, community, data, ' +
        'many becoming one, or something emerging.',
    checkLooksFor:
        'a <canvas> whose particles target the pixels of the word drawn with fillText and read back with getImageData, redrawn from an onUpdate',
    documentUsesSkill: check(
        (script, markup) =>
            /<canvas\b/.test(markup) &&
            /fillText/.test(script) &&
            /getImageData/.test(script) &&
            /onUpdate/.test(script),
    ),
    reviewNote:
        'A cloud of grainy dots, or a word whose letters are made of speckled grain, is this ' +
        'technique working: the texture is the particles.',
    instructions: `# Particle Word Form

Thousands of tiny glowing particles hang in the frame as a **churning cloud**. On the beat they **stream into the letters of the word**, the left-hand letters first, so the word condenses out of the swarm from left to right while the rest of the cloud is still moving. Once formed, the letters are made of grain: speckled, slightly shimmering, unmistakably made of many.

Use it for a word about many becoming one: gather, swarm, crowd, community, team, data, emerge, form.

${KINETIC_WORD_SETTING}

The word here is drawn on a canvas, not set as HTML, so \`fitWord\` measures an invisible HTML copy of it; the copy's computed font is then used to draw the targets.

## Markup

\`\`\`html
<div id="pw-copy" class="absolute inset-0 flex items-center justify-center" aria-hidden="true">
  <div id="pw-word" data-l5-allow-literal style="visibility: hidden; color: var(--l5-color-1, #ff5a36); line-height: 1; white-space: nowrap; font-family: var(--l5-font-0), sans-serif; font-weight: 800; text-transform: uppercase;">SWARM</div>
</div>
<canvas id="pw-canvas" width="1920" height="1080" class="absolute inset-0" style="width: 100%; height: 100%;"></canvas>
\`\`\`

## Timeline code

The particles are a pure function of time: every redraw computes every particle's position from the timeline's progress and a seeded random, so any frame renders identically, in any order.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const SCENE = 2.4, CUE = 1.0;                     // the word forms on its cue
const W = 1920, H = 1080, N = 16000;
const canvas = document.querySelector('#pw-canvas');
const ctx = canvas.getContext('2d');
const word = document.querySelector('#pw-word');
// The letters' pixels become the targets. They are sampled on the next draw after every fit, so
// the refit when the video's font loads is sampled again in the real face.
let sampled = false;
fitWord(word, 0.86, () => { sampled = false; });

let seed = 11;
const rand = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
const parts = [];
for (let i = 0; i < N; i++) {
  const a = rand() * Math.PI * 2, r = Math.pow(rand(), 0.6);
  parts.push({ cx: Math.cos(a) * r, cy: Math.sin(a) * r, ph: rand(), tx: 0, ty: 0 });
}

function sampleTargets() {
  const style = getComputedStyle(word);
  const box = word.getBoundingClientRect();
  const off = document.createElement('canvas');
  off.width = W; off.height = H;
  const o = off.getContext('2d');
  o.font = style.fontWeight + ' ' + style.fontSize + ' ' + style.fontFamily;
  // Centred on the word's box by the letters' own ink: a tall face set from the top of the box
  // lands well above the middle of the frame.
  const text = word.textContent.toUpperCase();
  const ink = o.measureText(text);
  o.textAlign = 'center';
  o.fillText(text, box.left + box.width / 2,
    box.top + box.height / 2 + (ink.actualBoundingBoxAscent - ink.actualBoundingBoxDescent) / 2);
  const data = o.getImageData(0, 0, W, H).data;
  const inside = [];
  for (let y = 0; y < H; y += 2) for (let x = 0; x < W; x += 2) if (data[(y * W + x) * 4 + 3] > 128) inside.push([x, y]);
  parts.forEach((p, i) => { const t = inside[Math.floor(p.ph * inside.length + i) % inside.length]; p.tx = t[0]; p.ty = t[1]; });
  sampled = inside.length > 0;
}

const state = { t: 0 };
// The particles take the word's colour, which is the palette's accent.
const COLOR = getComputedStyle(word).color;
function draw() {
  if (!sampled) sampleTargets();
  const t = state.t;
  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = COLOR;
  const grow = Math.min(1, t / 0.4);
  const rx = 260 + 360 * grow, ry = 120 + 230 * grow;
  for (const p of parts) {
    // The cloud: an ellipse whose particles churn on two crossing waves.
    let x = W / 2 + p.cx * rx + 22 * Math.sin((p.cx + p.cy) * 6 + t * 5);
    let y = H / 2 + p.cy * ry + 22 * Math.sin((p.cx - p.cy) * 6 - t * 4);
    // Into the letters, left first.
    const start = CUE - 0.35 + (p.tx / W) * 0.3 + p.ph * 0.08;
    const k = Math.min(1, Math.max(0, (t - start) / 0.22));
    const e = k * k * (3 - 2 * k);
    x += (p.tx - x) * e; y += (p.ty - y) * e;
    // Grains, not pixels: a 1px dot vanishes once the frame is scaled down. Faint in the cloud,
    // solid once in a letter.
    ctx.globalAlpha = 0.35 + 0.65 * e;
    ctx.fillRect(x - 1.5, y - 1.5, 3, 3);
  }
  ctx.globalAlpha = 1;
}
tl.to(state, { t: SCENE, duration: SCENE, ease: 'none', onUpdate: draw }, 0);
window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Colour**: the particles take the word element's colour, so set that to the palette's accent (or the ink, on a light ground) and they follow it.
- **Dispersing**: run the formation backwards for scatter, dissolve, disappear.
- **Denser**: a heavier word or bigger frame wants more particles (up to about 20,000) and a tighter sampling step (2px).

## Pitfalls

- **Seeded, never \`Math.random()\`**, and every position derived from \`state.t\`: a frame must render the same however it is reached.
- **Sample again after every fit.** The first fit can run in the fallback font; \`fitWord\` fits the word again when the video's font loads, and clearing \`sampled\` in its \`onFit\` makes the next \`draw\` take the targets from the refitted word.
- **The canvas covers the frame**, at the frame's own pixel size, so no particle is stretched.
- **Grains you can see.** Each particle is a 3px square, and there are enough of them (12,000–20,000) that the formed letters read as solid from across a room. Single-pixel particles disappear when the video is shown smaller than full size.
- **Keep the word visible long enough.** The formation lands on the cue and holds; a word still forming when the beat ends was never read.`,
};
